import React, { useState, useEffect } from "react";
import { Award, User, Users, Eye, AlertCircle } from "lucide-react";

export default function GuardAwardPuzzle() {
  const [isSimpleMode, setIsSimpleMode] = useState(true);
  const peopleCount = isSimpleMode ? 9 : 28;
  const people = Array.from({ length: peopleCount }, (_, i) => i + 1);
  const weeks = ["第一周", "第二周", "第三周", "第四周"];
  const totalSlots = weeks.length * 5;

  const [assignments, setAssignments] = useState({
    第一周: Array(5).fill(null),
    第二周: Array(5).fill(null),
    第三周: Array(5).fill(null),
    第四周: Array(5).fill(null),
  });

  const [highlightedGuard, setHighlightedGuard] = useState(null);
  const [dragError, setDragError] = useState("");

  const resetAssignments = () => {
    setAssignments({
      第一周: Array(5).fill(null),
      第二周: Array(5).fill(null),
      第三周: Array(5).fill(null),
      第四周: Array(5).fill(null),
    });
    setHighlightedGuard(null);
  };

  useEffect(() => {
    resetAssignments();
  }, [isSimpleMode]);

  const onDrop = (e, week, idx) => {
    e.preventDefault();
    const personId = parseInt(e.dataTransfer.getData("text/plain"), 10);
    if (!personId) return;

    // 检查该周是否已经有这个人
    if (assignments[week].includes(personId)) {
      setDragError(`士兵 ${personId} 在${week}已经获奖了！`);
      setTimeout(() => setDragError(""), 3000);
      return;
    }

    setAssignments((prev) => {
      const updated = { ...prev };
      if (updated[week][idx] === null) {
        updated[week][idx] = personId;
      }
      return updated;
    });
    setDragError("");
  };

  const onDragOver = (e) => e.preventDefault();

  const removeAssignment = (week, idx) => {
    setAssignments((prev) => {
      const updated = { ...prev };
      updated[week][idx] = null;
      return updated;
    });
  };

  const [awardees, setAwardees] = useState([]);
  useEffect(() => {
    const counts = {};
    Object.values(assignments).forEach((slots) => {
      slots.forEach((id) => {
        if (id !== null) counts[id] = (counts[id] || 0) + 1;
      });
    });
    const winners = Object.entries(counts)
      .filter(([_, c]) => c >= 3)
      .map(([id]) => parseInt(id, 10));
    setAwardees(winners);
  }, [assignments]);

  const handleGuardClick = (guardId) => {
    setHighlightedGuard(highlightedGuard === guardId ? null : guardId);
  };

  const isGuardHighlighted = (guardId) => {
    return highlightedGuard === guardId;
  };

  const getGuardWeekPositions = (guardId) => {
    const positions = [];
    Object.entries(assignments).forEach(([week, slots]) => {
      slots.forEach((id, index) => {
        if (id === guardId) {
          positions.push({ week, position: index });
        }
      });
    });
    return positions;
  };

  return (
    <div className="p-6 bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 min-h-screen">
      {/* 标题和模式切换 */}
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
          🛡️ 守卫标兵评选系统
        </h1>
        <p className="text-gray-600 mb-4">
          总共有4周评比，每周选出5位优秀士兵，3次或以上优秀者可获得"守卫标兵"称号
        </p>

        <div className="flex justify-center items-center gap-4 mb-4">
          <button
            onClick={() => setIsSimpleMode(true)}
            className={`px-6 py-2 rounded-full transition-all duration-300 ${
              isSimpleMode
                ? "bg-indigo-500 text-white shadow-lg"
                : "bg-white text-indigo-500 border-2 border-indigo-500 hover:bg-indigo-50"
            }`}
          >
            <Users size={20} className="inline mr-2" />
            简化版 (9人)
          </button>
          <button
            onClick={() => setIsSimpleMode(false)}
            className={`px-6 py-2 rounded-full transition-all duration-300 ${
              !isSimpleMode
                ? "bg-indigo-500 text-white shadow-lg"
                : "bg-white text-indigo-500 border-2 border-indigo-500 hover:bg-indigo-50"
            }`}
          >
            <Users size={20} className="inline mr-2" />
            完整版 (28人)
          </button>
        </div>

        {/* 错误提示 */}
        {dragError && (
          <div className="flex items-center justify-center gap-2 bg-red-100 border border-red-300 text-red-700 px-4 py-2 rounded-lg inline-flex animate-pulse">
            <AlertCircle size={20} />
            <span>{dragError}</span>
          </div>
        )}
      </div>

      <div className="flex flex-col xl:flex-row gap-8 items-start">
        {/* 士兵列表 */}
        <div className="w-full xl:w-80 p-6 bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20">
          <h2 className="text-2xl font-semibold mb-6 text-center text-gray-800">
            👥 士兵列表
          </h2>
          <div className="flex flex-wrap gap-3 justify-center xl:justify-start">
            {people.map((id) => (
              <div
                key={id}
                draggable
                onDragStart={(e) =>
                  e.dataTransfer.setData("text/plain", id.toString())
                }
                className="flex items-center justify-center space-x-2 p-3 border-2 rounded-xl bg-gradient-to-br from-blue-100 to-cyan-100 hover:from-blue-200 hover:to-cyan-200 cursor-move transition-all duration-300 hover:scale-105 shadow-sm hover:shadow-md w-20 h-14"
              >
                <User size={16} className="text-blue-600" />
                <span className="text-lg font-bold text-blue-800">{id}</span>
              </div>
            ))}
          </div>
        </div>

        {/* 周榜单 - 表格布局 */}
        <div className="w-full xl:flex-1 bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-white/20 overflow-x-auto">
          <h3 className="text-2xl font-semibold mb-6 text-center text-gray-800 bg-gradient-to-r from-indigo-500 to-purple-500 text-white py-3 rounded-xl">
            🏆 每周优秀士兵
          </h3>
          <div className="space-y-4 min-w-fit">
            {weeks.map((week) => (
              <div key={week} className="flex items-center gap-4">
                <div className="w-20 text-center flex-shrink-0">
                  <span className="inline-block bg-gradient-to-r from-indigo-500 to-purple-500 text-white px-3 py-2 rounded-lg text-sm font-semibold whitespace-nowrap">
                    {week}
                  </span>
                </div>
                <div className="flex-1 grid grid-cols-5 gap-3 min-w-0">
                  {assignments[week].map((person, idx) => (
                    <div
                      key={idx}
                      onDrop={(e) => onDrop(e, week, idx)}
                      onDragOver={onDragOver}
                      className={`h-14 border-2 border-dashed rounded-lg flex items-center justify-center transition-all duration-300 min-w-16 ${
                        person && isGuardHighlighted(person)
                          ? "bg-yellow-200 border-yellow-400 shadow-lg scale-105"
                          : "bg-gray-50 hover:bg-gray-100 border-gray-300"
                      }`}
                    >
                      {person ? (
                        <span
                          onClick={() => removeAssignment(week, idx)}
                          className="flex items-center space-x-1 cursor-pointer text-indigo-700 hover:text-indigo-900 transition-colors duration-200"
                        >
                          <User size={16} />
                          <span className="font-semibold">{person}</span>
                        </span>
                      ) : (
                        <span className="text-gray-400 text-xs">拖拽到此</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 结果展示 */}
      <div className="mt-8 bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-8 border border-white/20">
        <h2 className="text-3xl font-semibold mb-4 text-center bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
          🎖️ 守卫标兵榜
        </h2>

        {awardees.length > 0 ? (
          <div className="space-y-4">
            <p className="text-lg text-center text-gray-700 mb-4">
              🎉 恭喜以下士兵获得"守卫标兵"称号（3次或以上优秀）：
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              {awardees.map((id) => {
                const positions = getGuardWeekPositions(id);
                return (
                  <div
                    key={id}
                    onClick={() => handleGuardClick(id)}
                    className={`relative p-4 rounded-xl cursor-pointer transition-all duration-300 transform hover:scale-105 w-44 ${
                      isGuardHighlighted(id)
                        ? "bg-gradient-to-br from-yellow-200 to-orange-200 shadow-2xl scale-105"
                        : "bg-gradient-to-br from-green-100 to-emerald-100 hover:from-green-200 hover:to-emerald-200 shadow-lg"
                    }`}
                  >
                    <div className="flex items-center justify-center space-x-2 mb-2">
                      <div className="p-1 bg-green-500 rounded-full">
                        <User size={16} className="text-white" />
                      </div>
                      <Award size={20} className="text-green-600" />
                      <span className="text-lg font-bold text-green-800">
                        士兵 {id}
                      </span>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-gray-600 mb-1">
                        获奖 {positions.length} 次
                      </p>
                      <div className="flex justify-center gap-1 flex-wrap">
                        {positions.map((pos, index) => (
                          <span
                            key={index}
                            className="px-1.5 py-0.5 bg-green-500 text-white text-xs rounded-full"
                          >
                            {pos.week}
                          </span>
                        ))}
                      </div>
                      {isGuardHighlighted(id) && (
                        <div className="mt-1 text-xs text-indigo-600 flex items-center justify-center gap-1">
                          <Eye size={12} />
                          <span>高亮位置</span>
                        </div>
                      )}
                    </div>
                    {isGuardHighlighted(id) && (
                      <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full animate-ping"></div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="mt-4 p-3 bg-blue-50 rounded-lg border-l-4 border-blue-400">
              <p className="text-blue-700 text-center text-sm">
                💡 提示：点击守卫标兵卡片可以高亮显示他们在各周榜单中的位置
              </p>
            </div>
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🤔</div>
            <p className="text-xl text-gray-500 mb-4">
              暂无士兵达到守卫标兵标准
            </p>
            <p className="text-gray-400">需要3次或以上优秀才能成为守卫标兵</p>
          </div>
        )}

        {/* 统计信息 */}
        <div className="mt-6 flex justify-center gap-8">
          <div className="flex items-center gap-2 bg-gradient-to-r from-blue-100 to-blue-200 px-4 py-2 rounded-lg">
            <div className="text-2xl font-bold text-blue-700">
              {
                Object.values(assignments)
                  .flat()
                  .filter((id) => id !== null).length
              }
            </div>
            <div className="text-blue-600 text-sm">已分配席位</div>
          </div>
          <div className="flex items-center gap-2 bg-gradient-to-r from-green-100 to-green-200 px-4 py-2 rounded-lg">
            <div className="text-2xl font-bold text-green-700">
              {awardees.length}
            </div>
            <div className="text-green-600 text-sm">守卫标兵</div>
          </div>
          <div className="flex items-center gap-2 bg-gradient-to-r from-purple-100 to-purple-200 px-4 py-2 rounded-lg">
            <div className="text-2xl font-bold text-purple-700">
              {totalSlots}
            </div>
            <div className="text-purple-600 text-sm">总席位数</div>
          </div>
        </div>
      </div>
      <footer className="mt-8 w-full text-center text-sm text-gray-500">
        © {new Date().getFullYear()} 孙维刚教育研究院-陈硕老师 保留所有权利。
      </footer>
    </div>
  );
}
